import { useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SparklesIcon,
  Loader2Icon,
  CheckIcon,
  PencilIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { Affiliate } from "../types";
import { AffiliateReferralDiscountAdjuster } from "./affiliate-referral-discount-adjuster";
import { useUpdateDisplayName, useUpdateReferralDiscount } from "../hooks/use-affiliate-data";

type AffiliateSettingsTabProps = {
  affiliate: Affiliate;
};

export const AffiliateSettingsTab = ({ affiliate }: AffiliateSettingsTabProps) => {
  const updateDisplayName = useUpdateDisplayName();
  const updateReferralDiscount = useUpdateReferralDiscount();
  const [isEditingName, setIsEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(affiliate.displayName || "");

  const handleSaveDisplayName = async () => {
    if (!displayName.trim()) {
      toast.error("Display name cannot be empty");
      return;
    }
    if (displayName.length > 30) {
      toast.error("Display name must be 30 characters or less");
      return;
    }

    try {
      await updateDisplayName.mutateAsync(displayName.trim());
      toast.success("Display name updated!");
      setIsEditingName(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update display name");
    }
  };

  return (
    <div className="space-y-6">
      {/* Row 1: Display Name (33%) + Commission Rates (33%) + Commission Split (33%) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Display Name - Compact */}
        <div className="rounded-xl bg-card p-4 border border-border/50 shadow-sm dark:shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold">Display Name</h3>
              <p className="text-xs text-muted-foreground">Shown on referral pages</p>
            </div>
            {!isEditingName && (
              <Button size="sm" variant="ghost" onClick={() => setIsEditingName(true)}>
                <PencilIcon className="size-4 mr-1" />
                Edit
              </Button>
            )}
          </div>

          {isEditingName ? (
            <div className="space-y-2">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
                maxLength={30}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveDisplayName}
                  disabled={updateDisplayName.isPending}
                  className="flex-1"
                >
                  {updateDisplayName.isPending ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <>
                      <CheckIcon className="size-4 mr-1" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingName(false);
                    setDisplayName(affiliate.displayName || "");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-muted/30 rounded-lg px-4 py-3">
              <span className="font-medium">
                {affiliate.displayName || (
                  <span className="text-muted-foreground italic">Not set</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Commission Rates - Compact, no yellow example */}
        <div className="rounded-xl bg-card p-4 border border-border/50 shadow-sm dark:shadow-none">
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Your Commission Rates</h3>
            <p className="text-xs text-muted-foreground">Rates set by admin</p>
          </div>

          <div className="space-y-3">
            {/* Main Commission Rate */}
            <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Your Commission
              </p>
              <p className="text-2xl font-bold text-emerald-500">
                {(affiliate.tradingFeeShare * 100).toFixed(0)}%
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                of ALL trading fees (earned forever)
              </p>
            </div>

            {/* Referral Rebate Info */}
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-2.5">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium">Referral Rebate</span>
                <span className="text-base font-bold text-blue-500">
                  {(affiliate.referralDiscount * 100).toFixed(0)}%
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Credited back to referrals after trades
              </p>
            </div>

            {/* Performance Bonus (Future) - only show if > 0 */}
            {affiliate.performanceBonusRate > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium">Performance Bonus</span>
                  <span className="text-base font-bold text-amber-500">
                    {(affiliate.performanceBonusRate * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Commission Split Adjuster - Compact mode */}
        <AffiliateReferralDiscountAdjuster
          affiliate={affiliate}
          compact
          onUpdate={async (discount) => { await updateReferralDiscount.mutateAsync(discount); }}
        />
      </div>

      {/* Row 2: Referral Benefits (33%) + Links (33%) + Account Info (33%) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Referral Benefits */}
        <div className="rounded-xl bg-card p-4 border border-border/50 shadow-sm dark:shadow-none">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Referral Benefits</h3>
            <p className="text-xs text-muted-foreground">What your referrals receive</p>
          </div>

          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Fee Discount
              </p>
              <p className="text-2xl font-bold">
                {affiliate.referralDiscount > 0
                  ? `${(affiliate.referralDiscount * 100).toFixed(0)}%`
                  : "None"}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {affiliate.referralDiscount > 0
                  ? `Your referrals get ${(affiliate.referralDiscount * 100).toFixed(0)}% off trading fees`
                  : "No fee discount for referrals"}
              </p>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Signup Bonus
              </p>
              <p className="text-2xl font-bold">
                {affiliate.referralBonus > 0
                  ? `$${formatCurrency(affiliate.referralBonus, 2)}`
                  : "None"}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {affiliate.referralBonus > 0
                  ? `Bonus credited to referral on first deposit`
                  : "No signup bonus for referrals"}
              </p>
            </div>
          </div>
        </div>

        {/* Referral Links */}
        <div className="rounded-xl bg-card p-4 border border-border/50 shadow-sm dark:shadow-none">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Your Links</h3>
            <p className="text-xs text-muted-foreground">Referral code and custom URL</p>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Referral Code
              </p>
              <div className="bg-muted/50 rounded-lg px-4 py-3 font-mono text-lg font-bold">
                {affiliate.refCode}
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <SparklesIcon className="size-3" />
                Custom URL Slug
              </p>
              {affiliate.vanitySlug ? (
                <div className="bg-gradient-to-r from-primary/10 to-transparent rounded-lg px-4 py-3 font-mono border border-primary/20">
                  /{affiliate.vanitySlug}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No custom URL set. Go to the Overview tab to create one.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="rounded-xl bg-card p-4 border border-border/50 shadow-sm dark:shadow-none">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Account Information</h3>
            <p className="text-xs text-muted-foreground">Your affiliate account details</p>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Affiliate ID</span>
              <span className="font-mono">{affiliate.id.slice(0, 8)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-semibold",
                affiliate.status === "active" && "bg-emerald-500/10 text-emerald-600",
                affiliate.status === "pending" && "bg-amber-500/10 text-amber-600",
                affiliate.status === "suspended" && "bg-rose-500/10 text-rose-600"
              )}>
                {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Member Since</span>
              <span>
                {new Date(affiliate.createdAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
            {affiliate.approvedAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Approved On</span>
                <span>
                  {new Date(affiliate.approvedAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

